















































import Vue, { VueConstructor } from 'vue'
import { Component, Prop } from 'vue-property-decorator'

import { noNotificationView, NotificationSectionViewFactory } from '@/components/molecules/notification-section/model/notification-section-model'
import { log } from '@/log'
import { SalePriceDisplayType } from '@/models/api/productclass'
import BehaviorConfig from '@/models/app-config/behavior/behavior'
import CurrentTime from '@/models/current-time/CurrentTime'
import { create as createCurrentTime } from '@/models/current-time/functions'
import { AssociationService } from '@/services/api/external-account-association/association'
import { EventBusForThirdParty } from '@/usecases/event-bus-for-third-party/event-bus'
import { BrandRouteResolver } from '@/util/brandRouteResolver'

import { moveCarousel } from '../item-detail/interaction/move-carousel'
import { ActionButtonViewModel, createActionButtonViewModel, RequestContext } from './apparel-add-to-cart-area/action-button-view-model'
import ActionButton from './apparel-add-to-cart-area/ActionButton.vue'
import ApparelAddToCartAreaSkeleton from './apparel-add-to-cart-area/ApparelAddToCartAreaSkeleton.vue'
import BundleDiscountSection from './apparel-add-to-cart-area/BundleDiscountSection.vue'
import ColorSelection from './apparel-add-to-cart-area/ColorSelection.vue'
import { Fku, Sku } from './apparel-add-to-cart-area/model/item'
import { FkuListRepository } from './apparel-add-to-cart-area/model/item-repository'
import { prepareCartBeforeAddToCart } from './apparel-add-to-cart-area/prepare-cart-before-add-to-cart'
import Price from './apparel-add-to-cart-area/Price.vue'
import { createProductDetailViewEvent } from './apparel-add-to-cart-area/product-detail-view-event'
import QuantitySelection from './apparel-add-to-cart-area/QuantitySelection.vue'
import { ApparelAddToCartSelectionState } from './apparel-add-to-cart-area/selection-state'
import { createSizeSelectionDisplayPolicy, SizeSelectionDisplayPolicy } from './apparel-add-to-cart-area/size-selection-display-policy'
import SizeSelectionHistoryRepository from './apparel-add-to-cart-area/size-selection-history-repository'
import SizeSelection from './apparel-add-to-cart-area/SizeSelection.vue'
import {
  createSkuStatusLabelUiText,
  SkuStatusLabelUiText,
} from './apparel-add-to-cart-area/ui-text/sku-status-label'

// このコンポーネントが1ページ中に複数埋め込まれたときに
// 子コンポーネントのradioボタンが正しく動作するように制御するためのid
// とは言え基本的にこのコンポーネントが複数埋め込まれることは想定していない
let apparelAddToCartAreaInstanceCount = 0

@Component({
  components: {
    BundleDiscountSection,
    ColorSelection,
    SizeSelection,
    Price,
    ActionButton,
    ApparelAddToCartAreaSkeleton,
    QuantitySelection,
  },
})
export default class ApparelAddToCartArea extends Vue {
  @Prop({ required: true })
  readonly rootId: string

  @Prop({ required: true })
  initialDisplayedFkuId: string

  @Prop({ required: true })
  readonly salePriceDisplayType: SalePriceDisplayType

  @Prop({ required: true })
  readonly rootName: string

  @Prop({ required: true })
  readonly rootBrandEnglishName: string

  @Prop({ required: true })
  readonly rootCategoryPathnames: string[]

  @Prop({ default: () => createCurrentTime() })
  readonly current: CurrentTime

  isLoading: boolean = true
  selectionState: ApparelAddToCartSelectionState | null = null

  isLineAccountAssociated: boolean | null = null
  lineAssociationLoadError: boolean = false

  readonly instanceId: string = (() => {
    apparelAddToCartAreaInstanceCount += 1

    return `${apparelAddToCartAreaInstanceCount}`
  })()

  async created() {
    try {
      const initialPreferredSizePathname = this.sizeSelectionHistoryRepository.load()
      const fkuListRepository = FkuListRepository.create(this.brandEnglishName)
      const [
        fkuList,
      ] = await Promise.all([
        fkuListRepository.get(this.rootId),
        prepareCartBeforeAddToCart(this.brandEnglishName),
      ])
      this.selectionState = ApparelAddToCartSelectionState.create(
        fkuList,
        this.initialDisplayedFkuId,
        initialPreferredSizePathname
      )
      this.publishProductDetailViewEvent(fkuList)
    } catch (error) {
      log.error('failed to initialize apparel add-to-cart area:', error)
    } finally {
      this.isLoading = false
    }

    this.loadAccountAssociation()
  }

  get brandEnglishName(): string {
    return BrandRouteResolver.resolveBrandFromPathElement(this.$route.params.brand_english_name)
  }

  get sizeSelectionHistoryRepository(): SizeSelectionHistoryRepository {
    return new SizeSelectionHistoryRepository(this.brandEnglishName)
  }

  get behavior(): BehaviorConfig {
    return BehaviorConfig.createFromBrand(this.brandEnglishName)
  }

  get skuStatusLabelText(): SkuStatusLabelUiText {
    return createSkuStatusLabelUiText(this.behavior)
  }

  get sizeSelectionDisplayPolicy(): SizeSelectionDisplayPolicy {
    return createSizeSelectionDisplayPolicy(this.behavior)
  }

  get actionButtonViewModel(): ActionButtonViewModel {
    return createActionButtonViewModel(
      this.selectionState ? this.selectionState.selectedFku.map(fku => fku.id).toNullable() : null,
      this.selectionState ? this.selectionState.selectedSku.toNullable() : null,
      this.selectionState ? this.selectionState.selectedQuantity : 1,
      this.current,
      this.requestContext
    )
  }

  get requestContext(): RequestContext {
    return {
      isLineAccountAssociated: this.isLineAccountAssociated,
      lineAssociationLoadError: this.lineAssociationLoadError,
    }
  }

  // ロードが終わっているのにstateがnullであればエラーがあったと見なす
  get hasError(): boolean {
    return !this.isLoading && !this.selectionState
  }

  get errorSection(): VueConstructor {
    if (!this.hasError) {
      return noNotificationView
    }

    return NotificationSectionViewFactory.create('alert', {
      type: 'with-title',
      title: '商品情報が取得できませんでした',
      body: '時間をおいてページを再読み込みしてください。',
    })
  }

  onFkuSelected(fku: Fku) {
    if (!this.selectionState) {
      return
    }

    this.selectionState.selectFku(fku.id)
    moveCarousel(fku.representativeImageId)
  }

  onSkuSelected(sku: Sku) {
    if (!this.selectionState) {
      return
    }

    this.selectionState.selectSku(sku.id)
    if (sku.sizePathname) {
      this.sizeSelectionHistoryRepository.save(sku.sizePathname)
    }
  }

  onQuantitySelected(quantity: number) {
    if (!this.selectionState) {
      return
    }

    this.selectionState.selectQuantity(quantity)
  }

  private async loadAccountAssociation(): Promise<void> {
    return new AssociationService().list(this.brandEnglishName, { serviceId: 'line' })
      .then((result) => {
        result.fold(
          (error) => {
            this.isLineAccountAssociated = false
            this.lineAssociationLoadError = true
            log.warn('Optional API failed - Business logic error in AssociationService:', error)
          },
          (list) => {
            this.isLineAccountAssociated = list.length > 0
          }
        )
      })
      .catch((error) => {
        this.isLineAccountAssociated = false
        this.lineAssociationLoadError = true
        log.warn('Optional API failed - Network/Server error in AssociationService:', error)
      })
  }

  private publishProductDetailViewEvent(fkuList: Fku[]): void {
    /**
     * 商品詳細表示イベントはSKU情報を必要とするため、FKU取得済みのカート追加エリアで暫定的に発火している。
     * 将来的には商品詳細画面そのものの表示イベントとして、より適切な発火場所へ移動することが望ましい。
     */
    EventBusForThirdParty.getInstance().publish(
      'product-detail-view',
      createProductDetailViewEvent(
        {
          name: this.rootName,
          brandEnglishName: this.rootBrandEnglishName,
          categoryPathnames: this.rootCategoryPathnames,
        },
        fkuList,
        this.initialDisplayedFkuId
      )
    )
  }
}
